import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import baseURL, { appName } from '../../../configs/api';
import { jsPDF } from "jspdf";
import Loading from '../../../icons/Loading';
import '../../../styles/RentalAgreementView.css';
import { getFileUploads } from '../../../services/appFileUpload';
import Cookies from 'js-cookie';
import useTenant from '../../../components/hooks/useTenant';


const PrintRentalAgreementView = () => {
    const { handleTenantError }: any = useTenant();
    const navigate = useNavigate();
        const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const token = Cookies.get("token-jj-rental-ui");
    if (!token) {
        navigate('not-found');
    }

    const { search } = useLocation();
    const queryParams = new URLSearchParams(search)
    const reservationId = queryParams?.get('id') ?? 0;

    const [rentalAgreementHtmlCode, setRentalAgreementHtmlCode] = useState<string>("");
    const [initialsURL, setInitialsURL] = useState<any | null>();
    const [isFileLoaded, setIsFileLoaded] = useState<boolean>(false);

    const [isLoadingTheRentalAgrement, setIsLoadingTheRentalAgrement] = useState<boolean>(false);
    const htmlRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (Number(reservationId) > 0) {
            const fetchPdf = async () => {
                try {
                    const response = await axios.get(
                        `${baseURL}rentalAgreementPdf?reservationId=${reservationId}`,
                        {
                            responseType: 'text', // Fetching HTML content (not binary data)
                            headers: {
                                 'X-Tenant-ID': tenantId,
                            }
                        }
                    );

                    setRentalAgreementHtmlCode(response?.data || "");
                    handleTenantError(null);
                } catch (error) {
                    setIsLoadingTheRentalAgrement(true);
                    setRentalAgreementHtmlCode("")
                    handleTenantError(error);
                }
            }

            fetchPdf();

            getFileUploads("Reservation", reservationId?.toString(), tenantId)
                .then((data) => {
                    setInitialsURL(data?.find((item: any) => item?.tabName === "Initials"));
                    setIsFileLoaded(true);
                    handleTenantError(null);
                })
                .catch((error) => {
                    setIsFileLoaded(true);
                    handleTenantError(error);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservationId])

    useEffect(() => {
        if (isFileLoaded && rentalAgreementHtmlCode) {
            const convertPdf = async () => {
                try {
                    const getImageBase64 = async (imgURL: string) => {
                        const response = await fetch(imgURL, {
                            headers: {
                                 'X-Tenant-ID': tenantId,
                            }
                        });
                        const blob = await response.blob();
                        const reader = new FileReader();

                        return new Promise((resolve, reject) => {
                            reader.onloadend = () => {
                                resolve(reader.result);
                            };
                            reader.onerror = reject;
                            reader.readAsDataURL(blob);
                        });
                    };

                    const doc = new jsPDF({
                        orientation: "portrait",
                        unit: "mm",
                        format: "a4",
                        compress: true
                    });

                    // Set PDF properties
                    doc.setProperties({
                        title: `RentalAgreement${reservationId}.pdf`,
                        author: "Website",
                        subject: "Rental Agreement",
                        keywords: "rental, agreement",
                        creator: appName
                    });

                    let base64Image: string | null = null;
                    if (initialsURL?.fileName) {
                        const imgURL = `${baseURL}reservationFile/Get?name=${initialsURL?.fileName}`;
                        base64Image = await getImageBase64(imgURL) as string;
                    }

                    const addFooterImage = (doc: jsPDF, base64Image: string | null) => {
                        if (base64Image) {
                            const pageHeight = doc.internal.pageSize.height;
                            const pageWidth = doc.internal.pageSize.width

                            const reducedWidth = 8;
                            const reducedHeight = 4;

                            const xPosition = pageWidth - 24;
                            const yPosition = pageHeight - reducedHeight - 2;

                            doc.addImage(base64Image, 'JPEG', xPosition, yPosition, reducedWidth, reducedHeight);
                        }
                    }

                    const addPageNumber = (doc: jsPDF, pageIndex: number) => {
                        const pageHeight = doc.internal.pageSize.height;
                        const pageWidth = doc.internal.pageSize.width;

                        const totalPages = doc.internal.pages.length - 1;

                        const pageNumberText = `Page ${pageIndex} of ${totalPages}`;

                        const xPosition = pageWidth / 2 - doc.getTextWidth(pageNumberText) / 2;
                        const yPosition = pageHeight - 2;

                        doc.setFontSize(8);
                        doc.text(pageNumberText, xPosition, yPosition);
                    }

                    doc.html(`<div class="js-pdf-wrapper">${rentalAgreementHtmlCode}</div>`, {
                        callback: async (doc: jsPDF) => {
                            const totalPages = doc.internal.pages.length;

                            for (let i = 1; i < totalPages; i++) {
                                addFooterImage(doc, base64Image);
                                doc.setPage(i);
                                addPageNumber(doc, i);
                            }

                            const iframe = document.createElement('iframe');
                            iframe.src = doc.output('bloburi')?.toString();
                            iframe.setAttribute('style', 'width:100%;height:100vh');

                            if (htmlRef.current) {
                                htmlRef.current.innerHTML = '';
                                htmlRef.current.appendChild(iframe);
                                setIsLoadingTheRentalAgrement(true);
                            }
                        },
                        margin: [6, 5, 7, 5],
                        x: 0,
                        y: 0,
                        width: 200,
                        windowWidth: 1024,
                        filename: `RentalAgreement${reservationId}.pdf`,
                        autoPaging: "text"
                    });
                } catch (error) {
                    setIsLoadingTheRentalAgrement(true);
                    console.error("Error fetching or converting PDF:", error);
                }
            }
            convertPdf();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFileLoaded, rentalAgreementHtmlCode])


    return (
        <div className="w-full min-h-screen overflow-hidden">
            {
                !isLoadingTheRentalAgrement && (
                    <div className="w-full min-h-screen flex items-center justify-center">
                        <Loading fillColor={"#000"} />
                    </div>
                )
            }
            <div className="relative">
                <div className="w-full min-h-screen" ref={htmlRef}></div>
            </div>
        </div>
    )
}

export default PrintRentalAgreementView